<template>
  <div class="page-container">
    <div class="page-header">
      <div class="page-title-wrap">
        <h1>Tableau de Pointage</h1>
        <p class="page-subtitle">Suivi des présences et absences</p>
      </div>
    </div>

    <!-- Back button -->
    <button v-if="selectedStaff" class="btn-back" @click="back()">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <polyline points="15 18 9 12 15 6"></polyline>
      </svg>
      Retour
    </button>

    <!-- Search Staff -->
    <div class="search-section">
      <div class="search-card">
        <div class="search-wrap">
          <svg class="search-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input v-model="searchQuery" type="text" placeholder="Rechercher un employé..." @input="searchUsers"
            class="search-input" />
        </div>

        <!-- Search Results -->
        <div v-if="users && users.length" class="search-results">
          <div v-for="staff in users" :key="staff.Id" class="search-result-item" @click="fetchPointage(staff)">
            <img :src="staffImage(staff)" class="result-avatar" />
            <div>
              <div class="result-name">{{ staff.First_name }} {{ staff.Last_name }}</div>
              <div class="result-post">{{ staff.Post_name }}</div>
            </div>
            <svg class="result-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </div>
        </div>
        <div v-if="searchError" class="search-error">{{ searchError }}</div>
      </div>
    </div>

    <!-- Individual Staff Pointage -->
    <div v-if="selectedStaff" class="pointage-section">
      <div class="staff-banner">
        <img :src="staffImage(selectedStaff)" class="banner-avatar" />
        <div class="banner-info">
          <h2>{{ selectedStaff.First_name ? selectedStaff.First_name : selectedStaff.first_name }} {{
            selectedStaff.Last_name ? selectedStaff.Last_name : selectedStaff.last_name }}</h2>
          <span class="staff-post-badge">{{ selectedStaff.Post_name ? selectedStaff.Post_name : selectedStaff.post_name
            }}</span>
        </div>
        <div class="month-select-wrap">
          <label class="form-label">Mois</label>
          <MonthPickerButton v-model="selectedMonth" @change="fetchPointage(selectedStaff)" />
        </div>
      </div>

      <div v-if="fetchError && !records.length" class="info-notice">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
        {{ fetchError }}
      </div>

      <div v-if="loading" class="state-view">
        <div class="spinner-ring"></div>
        <p>Chargement...</p>
      </div>

      <div v-else-if="records.length" class="table-card">
        <div class="pointage-mobile-list">
          <article v-for="record in records" :key="record.id || `${record.date}-${record.time_in}`" class="pointage-mobile-card">
            <div class="mobile-card-head">
              <div>
                <span class="date-chip">{{ convertdate(record.date) }}</span>
                <p class="mobile-card-kicker">{{ record.time_out ? 'Pointage clôturé' : 'Pointage ouvert' }}</p>
              </div>
              <button class="icon-danger-btn" type="button" title="Supprimer le pointage" @click="deletePointage(record)">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M3 6h18" /><path d="M8 6V4h8v2" /><path d="m19 6-1 14H6L5 6" /><path d="M10 11v5M14 11v5" />
                </svg>
              </button>
            </div>
            <div class="mobile-time-grid">
              <div>
                <span>Entrée</span>
                <strong>{{ convertclock(record.time_in) }}</strong>
              </div>
              <div>
                <span>Sortie</span>
                <strong v-if="record.time_out">{{ convertclock(record.time_out) }}</strong>
                <strong v-else class="pending-text">En cours</strong>
              </div>
            </div>
            <div class="mobile-card-foot">
              <span>Durée</span>
              <strong v-if="record.time_in && record.time_out" class="hours-badge">{{ calculateHours(record.time_in, record.time_out) }}</strong>
              <strong v-else class="text-muted">-</strong>
            </div>
          </article>
          <div class="mobile-total-card">
            <div><span>Heures chargées</span><strong>{{ totalHours }}</strong></div>
            <div><span>Pointages</span><strong>{{ records.length }} / {{ pointageTotalCount }}</strong></div>
          </div>
        </div>
        <div class="table-responsive desktop-table">
          <table class="data-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Entrée</th>
                <th>Sortie</th>
                <th>Durée</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(record, index) in records" :key="index">
                <td>
                  <span class="date-chip">{{ convertdate(record.date) }}</span>
                </td>
                <td>
                  <div v-if="editIndex === index">
                    <input type="datetime-local" v-model="record.time_in" class="form-input datetime-input" />
                  </div>
                  <span v-else class="time-val">{{ convertclock(record.time_in) }}</span>
                </td>
                <td>
                  <div v-if="editIndex === index">
                    <input type="datetime-local" v-model="record.time_out" class="form-input datetime-input" />
                  </div>
                  <span v-else-if="record.time_out" class="time-val">{{ convertclock(record.time_out) }}</span>
                  <span v-else class="pending-badge">En cours</span>
                </td>
                <td>
                  <span v-if="record.time_in && record.time_out" class="hours-badge">
                    {{ calculateHours(record.time_in, record.time_out) }}
                  </span>
                  <span v-else class="text-muted">—</span>
                </td>
                <td>
                  <button class="icon-danger-btn" type="button" title="Supprimer le pointage" @click="deletePointage(record)">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M3 6h18" /><path d="M8 6V4h8v2" /><path d="m19 6-1 14H6L5 6" /><path d="M10 11v5M14 11v5" />
                    </svg>
                  </button>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td colspan="3" style="text-align:right;">Heures chargées</td>
                <td><span class="hours-badge">{{ totalHours }}</span></td>
                <td></td>
              </tr>
              <tr>
                <td colspan="3" style="text-align:right;">Pointages chargés</td>
                <td><strong>{{ records.length }} / {{ pointageTotalCount }}</strong></td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
        <div ref="pointageSentinel" class="pointage-load-state">
          <span v-if="loadingMorePointage">Chargement plus...</span>
          <span v-else-if="pointageHasMore">Faites défiler pour charger plus</span>
          <span v-else>Tous les pointages sont affichés</span>
        </div>
      </div>

      <div v-if="selectedStaff" class="table-card">
        <div v-if="remarks.length" class="remarks-mobile-list">
          <article v-for="remark in remarks" :key="remark.id" class="remark-mobile-card">
            <span class="date-chip">{{ formatRemarkDate(remark.created_at) }}</span>
            <p>{{ remark.remarque }}</p>
          </article>
        </div>
        <div v-else class="mobile-empty-line">Aucune remarque pour ce mois</div>
        <div class="table-responsive desktop-table">
          <table class="data-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Remarque</th>
              </tr>
            </thead>
            <tbody v-if="remarks.length">
              <tr v-for="remark in remarks" :key="remark.id">
                <td><span class="date-chip">{{ formatRemarkDate(remark.created_at) }}</span></td>
                <td><span class="time-val">{{ remark.remarque }}</span></td>
              </tr>
            </tbody>
            <tbody v-else>
              <tr>
                <td colspan="2" class="text-muted" style="text-align:center; padding: 14px;">Aucune remarque pour ce mois</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Mass Pointage Table -->
    <div v-if="showMass" class="mass-section">
      <div class="section-title-wrap">
        <h2>Vue d'ensemble mensuelle</h2>
        <div class="filter-controls">
          <div class="filter-group">
            <label class="filter-label">Poste</label>
            <select v-model="selectedPost" class="filter-select" @change="fetchPointageMass">
              <option value="">Tous les postes</option>
              <option v-for="post in posts" :key="post.Post_name" :value="post.Post_name">
                {{ post.Post_name }}
              </option>
            </select>
          </div>
          <div class="filter-group">
            <label class="filter-label">Mois</label>
            <MonthPickerButton v-model="selectedMonth" @change="fetchPointageMass" />
          </div>
        </div>
      </div>

      <div v-if="fetchMassError" class="info-notice">{{ fetchMassError }}</div>

      <div v-else-if="staffData.length" class="table-card">
        <div class="mass-mobile-list">
          <article v-for="staff in staffData" :key="staff.id" class="mass-mobile-card" @click="fetchPointage(staff)">
            <div class="mass-staff-head">
              <div class="mass-avatar">
                <img :src="staffImage(staff)" :alt="staff.first_name" />
              </div>
              <div class="mass-staff-main">
                <h3>{{ staff.first_name }} {{ staff.last_name }}</h3>
                <span>{{ staff.post_name }}</span>
              </div>
              <svg class="mobile-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M9 18l6-6-6-6" />
              </svg>
            </div>
            <div class="mass-stats-row">
              <div>
                <span>Jours</span>
                <strong>{{ staff.days_count }}j</strong>
              </div>
              <div>
                <span>Heures</span>
                <strong>{{ formatHours(staff.total_hours.toFixed(2)) }}</strong>
              </div>
              <div>
                <span>Absences</span>
                <strong :class="{ danger: staff.absence_count > 0 }">{{ staff.absence_count }}</strong>
              </div>
              <div>
                <span>Remarques</span>
                <strong>{{ staff.remarques_count }}</strong>
              </div>
            </div>
          </article>
        </div>
        <div class="table-responsive desktop-table">
          <table class="data-table mass-table">
            <thead>
              <tr>
                <th>Poste</th>
                <th>Employé</th>
                <th>Jours</th>
                <th>Heures</th>
                <th>Absences</th>
                <th>Remarques</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="group in groupedStaff" :key="group.postName">
                <tr v-for="(staff, index) in group.staffList" :key="staff.id">
                  <td v-if="index === 0" :rowspan="group.count" >
                    <span class="post-name-chip" >
                      {{ group.postName }}
                    </span>
                  </td>
                  <td>
                    <div class="staff-name-cell" @click="fetchPointage(staff)">
                      <img :src="staffImage(staff)" :alt="staff.first_name" />
                      <span>
                        <strong>{{ staff.first_name }} {{ staff.last_name }}</strong>
                        <small>{{ staff.post_name }}</small>
                      </span>
                    </div>
                  </td>
                  <td><span class="stat-chip chip-blue">{{ staff.days_count }} j</span></td>
                  <td><span class="stat-chip chip-purple">{{ formatHours(staff.total_hours.toFixed(2)) }}</span></td>
                  <td>
                    <span class="stat-chip" :class="staff.absence_count > 0 ? 'chip-red' : 'chip-green'">
                      {{ staff.absence_count }}
                    </span>
                  </td>
                  <td>
                    <span class="stat-chip" :class="staff.remarques_count > 0 ? 'chip-amber' : 'chip-green'">
                      {{ staff.remarques_count }}
                    </span>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MonthPickerButton from './MonthPickerButton.vue';

export default {
  inject: ['privilage'],
  name: 'AppStaffStat',
  components: { MonthPickerButton },
  data() {
    return {
      showMass: true, fetchMassError: null, staffData: [],
      selectedMonth: null, fetchError: null, searchError: null,
      selectedStaff: null, searchQuery: '', users: [],
      editIndex: null, editedRecord: null, records: [], loading: false, error: null,
      remarks: [],
      reportTitle: '', posts: [], selectedPost: '',
      pointagePage: 1,
      pointageLimit: 10,
      pointageTotalCount: 0,
      pointageHasMore: true,
      loadingMorePointage: false,
      pointageObserver: null,
    };
  },
  mounted() {
    const date = new Date();
    this.selectedMonth = this.$route.query.date ? String(this.$route.query.date).slice(0, 7) : `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    this.fetchPosts();
    this.fetchPointageMass();
    if (this.$route.query.staff_id) {
      this.openStaffFromQuery();
    }
  },
  beforeUnmount() {
    this.disconnectPointageObserver();
  },
  computed: {
    groupedStaff() {
      const map = {};
      this.staffData.forEach(s => { if (!map[s.post_name]) map[s.post_name] = []; map[s.post_name].push(s); });
      return Object.keys(map).map(postName => ({
        postName, staffList: [...map[postName]].sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`)), count: map[postName].length
      }));
    },
    totalHours() {
      let ms = 0;
      this.records.forEach(r => {
        if (r.time_in && r.time_out) {
          const s = new Date(r.time_in), e = new Date(r.time_out);
          if (!isNaN(s) && !isNaN(e) && e > s) ms += e - s;
        }
      });
      return `${Math.floor(ms / 3600000)}h ${Math.floor((ms % 3600000) / 60000)}m`;
    },
    totalDays() {
      return new Set(
        this.records
          .filter(r => r.time_in && r.time_out)
          .map(r => r.date)
      ).size;
    }
  },
  watch: {
    searchQuery(v) { if (v === '') { this.selectedStaff = null; this.fetchError = null; this.showMass = true; this.fetchPointageMass(); } }
  },
  methods: {
    groupByDate() {
      return this.records.reduce((acc, r) => {
        if (!acc[r.date]) {
          acc[r.date] = [];
        }
        acc[r.date].push(r);
        return acc;
      }, {});
    },
    async back() {
      this.disconnectPointageObserver();
      this.selectedStaff = null;
      this.fetchError = null;
      this.showMass = true;
    },
    async fetchPointageMass() {
      try {
        let url = `/api-pointage/getpointageMass?month=${this.selectedMonth}`;
        if (this.selectedPost) {
          url += `&post=${encodeURIComponent(this.selectedPost)}`;
        }
        const response = await fetch(url);
        const data = await response.json();
        console.log(data);

        if (data && Array.isArray(data)) {
          this.staffData = data.filter(s => !(s.days_count === 0 && s.absence_count === 0 && s.total_hours === 0 && s.remarques_count === 0));
          this.fetchMassError = this.staffData.length === 0 ? 'Aucun pointage pour ce mois!' : null;
        } else {
          this.staffData = [];
          this.fetchMassError = 'Aucun pointage pour ce mois!';
        }
      } catch (err) { console.error(err); this.staffData = []; }
    },
    async searchUsers() {
      this.records = [];
      if (!this.searchQuery.trim()) { this.users = []; return; }
      try {
        const res = await fetch(`/api-pointage/search?query=${this.searchQuery}`);
        if (!res.ok) throw new Error('Not found');
        const data = await res.json();
        const users = Array.isArray(data) ? data : [];
        if (users.length) { this.searchError = null; this.users = users; }
        else { this.users = []; this.searchError = 'Aucun employé trouvé'; }
      } catch { this.users = []; }
    },
    formatHours(d) { const h = Math.floor(d); return `${h}h ${Math.round((d - h) * 60)}m`; },
    calculateHours(s, e) {
      const ms = new Date(e) - new Date(s);
      if (isNaN(ms) || ms < 0) return '0h';
      return `${Math.floor(ms / 3600000)}h ${Math.floor((ms % 3600000) / 60000)}m`;
    },
    enableEdit(i) { this.editIndex = i; this.editedRecord = { ...this.records[i] }; },
    cancelEdit() { if (this.editIndex !== null) this.records[this.editIndex] = { ...this.editedRecord }; this.editIndex = null; this.editedRecord = null; },
    async deletePointage(record) {
      const staffId = this.selectedStaff?.Id || this.selectedStaff?.id;
      if (!record?.id || !staffId) return;
      const label = `${this.convertdate(record.date)} - ${this.convertclock(record.time_in)}${record.time_out ? ` / ${this.convertclock(record.time_out)}` : ' / en cours'}`;
      if (!confirm(`Supprimer ce pointage ?\n${label}`)) return;
      try {
        const response = await fetch('/api-pointage/deletpointage', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ rowId: record.id, staff_id: staffId }),
        });
        if (!response.ok) {
          const err = await response.json().catch(() => ({ error: 'Erreur' }));
          throw new Error(err.error || 'Erreur suppression pointage');
        }
        await this.fetchPointage(this.selectedStaff);
        await this.fetchPointageMass();
      } catch (err) {
        alert('Erreur: ' + err.message);
      }
    },
    async fetchPointage(staff, append = false) {
      if (!staff || (append && (!this.pointageHasMore || this.loadingMorePointage))) return;
      this.selectedStaff = staff;
      this.showMass = false;
      this.error = null;
      this.users = [];
      if (!append) {
        this.disconnectPointageObserver();
        this.pointagePage = 1;
        this.pointageTotalCount = 0;
        this.pointageHasMore = true;
        this.records = [];
        this.remarks = [];
        this.loading = true;
      } else {
        this.loadingMorePointage = true;
      }

      try {
        const staffId = staff.Id ? staff.Id : staff.id;
        const params = new URLSearchParams({
          staff_id: String(staffId),
          month: this.selectedMonth,
          page: String(this.pointagePage),
          limit: String(this.pointageLimit),
        });
        const requests = [fetch(`/api-pointage/getpointage?${params.toString()}`)];
        if (!append) {
          requests.push(fetch(`/api-pointage/getremarques?staff_id=${staffId}&month=${this.selectedMonth}`));
        }
        const [res, remRes] = await Promise.all(requests);

        if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Error'); }
        else this.fetchError = `Aucun pointage ce mois pour ${staff.First_name ? staff.First_name : staff.first_name} ${staff.Last_name ? staff.Last_name : staff.last_name}`;
        const rawData = await res.json();
        const data = this.sortPointageRecords((Array.isArray(rawData) ? rawData : []).map(r => ({
          ...r,
          time_in: r.time_in ? this.formatForInput(r.time_in) : '',
          time_out: r.time_out ? this.formatForInput(r.time_out) : ''
        })));
        this.pointageTotalCount = parseInt(res.headers.get('X-Total-Count') || String(append ? this.pointageTotalCount : data.length), 10);

        if (remRes && remRes.ok) {
          const remarksData = await remRes.json();
          this.remarks = Array.isArray(remarksData) ? remarksData : [];
        }

        if (data.length) {
          this.fetchError = null;
          this.records = this.sortPointageRecords(append ? [...this.records, ...data] : data);
          this.pointagePage += 1;
          this.pointageHasMore = this.records.length < this.pointageTotalCount;
        } else if (!append) {
          this.records = [];
          this.pointageHasMore = false;
        } else {
          this.pointageHasMore = false;
        }
        if (!this.records.length) this.fetchError = `Aucun pointage ce mois pour ${staff.First_name ? staff.First_name : staff.first_name} ${staff.Last_name ? staff.Last_name : staff.last_name}`;
      } catch (err) { console.log(err.message); }
      finally {
        this.loading = false;
        this.loadingMorePointage = false;
        this.$nextTick(this.setupPointageObserver);
      }
    },
    loadMorePointage() {
      return this.fetchPointage(this.selectedStaff, true);
    },
    setupPointageObserver() {
      this.disconnectPointageObserver();
      if (typeof IntersectionObserver === 'undefined' || !this.$refs.pointageSentinel || !this.pointageHasMore) return;
      this.pointageObserver = new IntersectionObserver((entries) => {
        if (entries.some(entry => entry.isIntersecting)) {
          this.loadMorePointage();
        }
      }, { rootMargin: '160px 0px' });
      this.pointageObserver.observe(this.$refs.pointageSentinel);
    },
    disconnectPointageObserver() {
      if (this.pointageObserver) {
        this.pointageObserver.disconnect();
        this.pointageObserver = null;
      }
    },
    sortPointageRecords(records) {
      return [...records].sort((a, b) => {
        const left = new Date(a.time_in || `${a.date}T00:00:00`).getTime();
        const right = new Date(b.time_in || `${b.date}T00:00:00`).getTime();
        if (right !== left) return right - left;
        return Number(b.id || 0) - Number(a.id || 0);
      });
    },
    formatForInput(s) { return s.replace(/\//g, '-').replace(' ', 'T'); },
    formatForServer(s) { return s.replace(/-/g, '/').replace('T', ' '); },
    convertdate(input) {
      if (!input) return '';
      const normalized = String(input).replace(/\//g, '-').slice(0, 10);
      const [y, m, d] = normalized.split('-');
      if (!y || !m || !d) return input;
      return `${d}/${m}/${y}`;
    },
    convertclock(input) {
      if (!input || input === 'absent') return input || '';
      const [date, time] = input.split('T');
      if (!date) return input;
      const [y, m, d] = date.split('-');
      return `${d}/${m}/${y} ${time || ''}`;
    },
    formatRemarkDate(input) {
      if (!input) return '';
      const normalized = input.replace(/\//g, '-').replace(' ', 'T');
      const [date, time] = normalized.split('T');
      if (!date) return input;
      const [y, m, d] = date.split('-');
      return `${d}/${m}/${y}${time ? ` ${time.slice(0, 5)}` : ''}`;
    },
    firstLetter(staff) {
      return (staff.first_name || staff.First_name || '?').charAt(0).toUpperCase();
    },
    staffImage(staff) {
      const pic = staff?.Pic || staff?.pic || '';
      if (!pic) return '/pointage/profile/profile.jpg';
      return pic.startsWith('/api-pointage/uploads/') || pic.startsWith('/pointage/')
        ? pic
        : `/api-pointage/uploads/${pic}`;
    },
    async fetchPosts() {
      try {
        const response = await fetch(`/api-pointage/fetchposts`, {
          credentials: 'include',
          cache: 'no-store',
        });
        if (!response.ok) throw new Error('Failed');
        const data = await response.json();
        if (data && Array.isArray(data)) {
          this.posts = data;
        } else {
          this.posts = [];
        }
      } catch (err) { console.error(err); this.posts = []; }
    },
    async openStaffFromQuery() {
      try {
        const staffId = this.$route.query.staff_id;
        const res = await fetch(`/api-pointage/staff-dossier?id=${encodeURIComponent(staffId)}&month=${this.selectedMonth}`, {
          credentials: 'include',
          cache: 'no-store',
        });
        if (!res.ok) return;
        const data = await res.json();
        if (data && data.staff) {
          await this.fetchPointage(data.staff);
        }
      } catch (err) {
        console.error('Erreur ouverture notification:', err);
      }
    }
  }
};
</script>

<style scoped>
.page-container {
  display: flex;
  flex-direction: column;
  gap: 24px;
  animation: slideUp 0.4s ease;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.page-title-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  text-align: center;
}

.page-header h1 {
  font-size: 24px;
  margin-bottom: 4px;
}

.page-subtitle {
  font-size: 13px;
  color: var(--gray-500);
}

.search-section {}

.search-card {
  background: var(--white);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  padding: 16px;
  box-shadow: var(--shadow-sm);
}

.search-wrap {
  position: relative;
}

.search-icon-svg {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  color: var(--gray-400);
  pointer-events: none;
}

.search-input {
  padding-left: 38px !important;
  background: var(--gray-50) !important;
}

.search-results {
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.search-result-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: background var(--transition);
}

.search-result-item:hover {
  background: var(--primary-glow);
}

.result-avatar {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  object-fit: cover;
  border: 2px solid var(--border);
}

.result-name {
  font-size: 13.5px;
  font-weight: 600;
  color: var(--gray-800);
}

.result-post {
  font-size: 12px;
  color: var(--gray-500);
}

.result-arrow {
  width: 16px;
  height: 16px;
  color: var(--gray-400);
  margin-left: auto;
}

.search-error {
  padding: 10px 14px;
  font-size: 13px;
  color: var(--warning);
}

.info-notice {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: var(--primary-glow);
  border: 1px solid rgba(26, 86, 219, 0.15);
  border-radius: var(--radius-sm);
  color: var(--primary);
  font-size: 13px;
}

.info-notice svg {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.staff-banner {
  background: var(--white);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  padding: 16px 20px;
  display: flex;
  align-items: center;
  gap: 14px;
  box-shadow: var(--shadow-sm);
}

.banner-avatar {
  width: 52px;
  height: 52px;
  border-radius: 12px;
  object-fit: cover;
  border: 2px solid var(--border);
}

.banner-info {
  flex: 1;
}

.banner-info h2 {
  font-size: 18px;
  margin-bottom: 5px;
}

.month-select-wrap {
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-end;
}

.staff-post-badge {
  display: inline-flex;
  padding: 3px 10px;
  background: var(--primary-glow);
  color: var(--primary);
  border-radius: var(--radius-full);
  font-size: 11.5px;
  font-weight: 600;
}

.table-card {
  background: var(--white);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  overflow: hidden;
  box-shadow: var(--shadow-sm);
}

.table-responsive {
  overflow-x: auto;
}

.pointage-mobile-list,
.remarks-mobile-list,
.mass-mobile-list {
  display: none;
}

.date-chip {
  display: inline-flex;
  padding: 3px 8px;
  background: var(--gray-100);
  border-radius: 5px;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--gray-700);
  white-space: nowrap;
}

.absent-chip {
  background: var(--danger-bg);
  color: var(--danger);
}

.time-val {
  font-size: 13px;
  color: var(--gray-700);
  font-weight: 500;
}

.pending-badge {
  display: inline-flex;
  padding: 3px 8px;
  background: var(--warning-bg);
  color: var(--warning);
  border-radius: 5px;
  font-size: 12px;
  font-weight: 600;
}

.hours-badge {
  display: inline-flex;
  padding: 3px 10px;
  background: var(--success-bg);
  color: var(--success);
  border-radius: 5px;
  font-size: 12px;
  font-weight: 700;
}

.icon-danger-btn {
  width: 34px;
  height: 34px;
  border: 1px solid rgba(239, 68, 68, 0.22);
  border-radius: 7px;
  background: var(--danger-bg);
  color: var(--danger);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.icon-danger-btn svg {
  width: 15px;
  height: 15px;
}

.icon-danger-btn:hover {
  background: var(--danger);
  color: #fff;
}

.text-muted {
  color: var(--gray-400);
}

.pointage-load-state {
  min-height: 46px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-top: 1px solid var(--border);
  color: var(--gray-500);
  font-size: 12.5px;
  font-weight: 800;
  background: var(--gray-50);
}

.section-title-wrap {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.section-title-wrap h2 {
  font-size: 20px;
}

.filter-controls {
  display: flex;
  gap: 16px;
  align-items: flex-end;
  flex-wrap: wrap;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.filter-label {
  font-size: 11px;
  font-weight: 600;
  color: var(--gray-500);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.filter-select {
  padding: 8px 12px;
  border: 1px solid var(--border);
  border-radius: 8px;
  font-size: 14px;
  color: var(--gray-800);
  background: var(--white);
  cursor: pointer;
  min-width: 180px;
}

.filter-select:focus {
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.post-name-chip {
  display: inline-flex;
  padding: 5px 12px;
  color: #000;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 700;
  white-space: nowrap;
}

.staff-name-cell {
  display: inline-grid;
  grid-template-columns: 38px minmax(0, 1fr);
  align-items: center;
  gap: 10px;
  min-width: 0;
  color: var(--gray-800);
  cursor: pointer;
}

.staff-name-cell img {
  width: 38px;
  height: 38px;
  border-radius: 8px;
  object-fit: cover;
  border: 1px solid var(--border);
  background: var(--gray-50);
}

.staff-name-cell span {
  display: grid;
  gap: 2px;
  min-width: 0;
}

.staff-name-cell strong {
  color: var(--gray-900);
  font-size: 13.5px;
  font-weight: 800;
}

.staff-name-cell small {
  color: var(--gray-500);
  font-size: 12px;
}

.stat-chip {
  display: inline-flex;
  padding: 3px 10px;
  border-radius: 5px;
  font-size: 12px;
  font-weight: 700;
}

.chip-blue {
  background: var(--primary-glow);
  color: var(--primary);
}

.chip-purple {
  background: rgba(124, 58, 237, 0.1);
  color: #7c3aed;
}

.chip-red {
  background: var(--danger-bg);
  color: var(--danger);
}

.chip-green {
  background: var(--success-bg);
  color: var(--success);
}

.chip-amber {
  background: var(--warning-bg);
  color: var(--warning);
}

.state-view {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  gap: 12px;
  color: var(--gray-400);
}

.spinner-ring {
  width: 36px;
  height: 36px;
  border: 3px solid var(--border);
  border-top-color: var(--primary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

th {
  font-weight: bold !important;
  color: black !important;
}

.btn-back {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  font-size: 14px;
  font-weight: 500;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: white;
  color: #374151;
  cursor: pointer;
  transition: all 0.25s ease;
}

.btn-back svg {
  width: 16px;
  height: 16px;
}

.btn-back:hover {
  background: #f9fafb;
  border-color: #d1d5db;
  transform: translateY(-1px);
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 640px) {
  .page-container {
    gap: 14px;
  }

  .page-header {
    align-items: center;
    justify-content: center;
    text-align: center;
    padding-left: 0;
  }

  .page-title-wrap {
    align-items: center;
  }

  .page-header h1 {
    font-size: 22px;
  }

  .search-card {
    padding: 10px;
  }

  .staff-banner {
    display: grid;
    grid-template-columns: 52px minmax(0, 1fr);
    align-items: center;
    padding: 12px;
    gap: 10px;
  }

  .banner-info h2 {
    font-size: 17px;
    line-height: 1.2;
  }

  .banner-avatar {
    width: 52px;
    height: 52px;
  }

  .staff-banner .month-select-wrap {
    grid-column: 1 / -1;
    width: 100%;
    align-items: stretch;
  }

  .month-select-wrap {
    align-items: stretch;
  }

  .section-title-wrap {
    align-items: stretch;
  }

  .section-title-wrap h2 {
    font-size: 18px;
  }

  .filter-controls,
  .filter-group,
  .filter-select {
    width: 100%;
    min-width: 0;
  }

  .desktop-table {
    display: none;
  }

  .pointage-mobile-list,
  .remarks-mobile-list,
  .mass-mobile-list {
    display: grid;
    gap: 10px;
    padding: 10px;
  }

  .pointage-mobile-card,
  .remark-mobile-card,
  .mass-mobile-card,
  .mobile-total-card {
    border: 1px solid var(--border);
    border-radius: 10px;
    background: var(--white);
    padding: 12px;
  }

  .mobile-card-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;
  }

  .mobile-card-kicker {
    margin-top: 5px;
    color: var(--gray-500);
    font-size: 12px;
    font-weight: 800;
  }

  .mobile-time-grid,
  .mobile-total-card {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    margin-top: 10px;
  }

  .mobile-time-grid > div,
  .mobile-total-card > div {
    display: grid;
    gap: 3px;
    padding: 10px;
    border-radius: 8px;
    background: var(--gray-50);
    min-width: 0;
  }

  .mobile-time-grid span,
  .mobile-total-card span,
  .mobile-card-foot span {
    font-size: 11px;
    font-weight: 800;
    color: var(--gray-500);
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .mobile-time-grid strong,
  .mobile-total-card strong {
    color: var(--gray-900);
    font-size: 13px;
    overflow-wrap: anywhere;
  }

  .mobile-card-foot {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px solid var(--border);
  }

  .pending-text,
  .mobile-stat-grid .danger {
    color: var(--danger);
  }

  .remark-mobile-card {
    display: grid;
    gap: 8px;
  }

  .remark-mobile-card p {
    color: var(--gray-700);
    line-height: 1.45;
  }

  .mobile-empty-line {
    padding: 16px;
    text-align: center;
    color: var(--gray-500);
    font-weight: 700;
  }

  .mass-mobile-card {
    cursor: pointer;
    display: grid;
    gap: 12px;
    padding: 12px;
    border-radius: 8px;
    box-shadow: 0 1px 4px rgba(15, 23, 42, 0.06);
    transition: transform var(--transition), box-shadow var(--transition);
  }

  .mass-mobile-card:active {
    transform: scale(0.99);
  }

  .mass-staff-head {
    display: grid;
    grid-template-columns: 76px minmax(0, 1fr) 20px;
    gap: 12px;
    align-items: center;
  }

  .mass-avatar {
    width: 76px;
    height: 76px;
    border-radius: 10px;
    background: #f1f5f9;
    color: var(--primary);
    border: 1px solid var(--border);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    font-size: 26px;
    font-weight: 900;
  }

  .mass-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .mass-staff-main {
    min-width: 0;
  }

  .mass-staff-main h3 {
    font-size: 16px;
    line-height: 1.2;
    font-weight: 900;
    color: var(--gray-900);
    margin: 0;
    overflow-wrap: anywhere;
  }

  .mass-staff-main span {
    display: inline-block;
    margin-top: 6px;
    color: var(--primary);
    background: var(--primary-glow);
    border-radius: 6px;
    padding: 4px 8px;
    font-size: 12px;
    font-weight: 800;
  }

  .mass-stats-row {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 6px;
    padding-top: 10px;
    border-top: 1px solid var(--border);
  }

  .mass-stats-row div {
    min-width: 0;
    display: grid;
    gap: 3px;
  }

  .mass-stats-row span {
    color: var(--gray-500);
    font-size: 10px;
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: 0;
  }

  .mass-stats-row strong {
    color: var(--gray-900);
    font-size: 13px;
    font-weight: 900;
    overflow-wrap: anywhere;
  }

  .mass-stats-row .danger {
    color: var(--danger);
  }

  .mobile-arrow {
    width: 20px;
    height: 20px;
    color: var(--gray-400);
    flex: 0 0 20px;
  }
}
</style>
